import { getFunctions, httpsCallable, type Functions } from 'firebase/functions'
import { firebaseApp, firebaseFunctions, REGION } from './firebase'
import { getActiveSessionId } from '../utils/debugSessionRecorder'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces'
import { getEduDemoResponder } from './eduDemo/eduDemoRegistry'
import { OPS_CALLABLE_HANDLERS, OPS_CALLABLE_REGION } from './functionRegions.generated'

export type CallableOptions = {
  timeoutMs?: number
  retries?: number
  retryOnCodes?: FunctionsErrorCode[]
  idempotencyKey?: string
  signal?: AbortSignal
  region?: string
  suppressLogging?: boolean
  functions?: Functions
}

// The modular Firebase Functions SDK exposes this code only on its compat
// surface, so retain the SDK's canonical callable error vocabulary locally.
export type FunctionsErrorCode =
  | 'cancelled' | 'unknown' | 'invalid-argument' | 'deadline-exceeded'
  | 'not-found' | 'already-exists' | 'permission-denied' | 'resource-exhausted'
  | 'failed-precondition' | 'aborted' | 'out-of-range' | 'unimplemented'
  | 'internal' | 'unavailable' | 'data-loss' | 'unauthenticated' | 'app-check-failed'

export type CallableError = {
  code: FunctionsErrorCode
  message: string
  details?: unknown
  handler: string
  attempt: number
  totalAttempts: number
  latencyMs: number
  idempotencyKey: string
  cause?: unknown
}

type CallableTelemetryLogger = {
  error: (event: string, context: Record<string, unknown>) => void
  debug: (event: string, context: Record<string, unknown>) => void
}

// The full telemetry pipeline lands independently. This seam intentionally
// fails open until then, while keeping the wrapper's logging contract stable.
let telemetryLogger: CallableTelemetryLogger = { error: () => undefined, debug: () => undefined }

export const setCallableTelemetryLoggerForTests = (logger: CallableTelemetryLogger | undefined) => {
  telemetryLogger = logger ?? { error: () => undefined, debug: () => undefined }
}

let cachedOpsCallableFunctions: Functions | undefined

const getCachedOpsCallableFunctions = (): Functions => {
  if (!cachedOpsCallableFunctions) {
    cachedOpsCallableFunctions = getFunctions(firebaseApp, OPS_CALLABLE_REGION)
  }
  return cachedOpsCallableFunctions
}

export const resetOpsCallableFunctionsCacheForTests = (): void => {
  cachedOpsCallableFunctions = undefined
}

const DEFAULT_RETRY_CODES: FunctionsErrorCode[] = ['unavailable', 'deadline-exceeded', 'internal']

const createIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `call-${Date.now()}-${Math.random().toString(36).slice(2)}`

const errorCode = (error: unknown): FunctionsErrorCode => {
  const raw = typeof error === 'object' && error && 'code' in error ? String(error.code) : 'internal'
  return raw.replace(/^functions\//, '') as FunctionsErrorCode
}

const errorMessage = (error: unknown) =>
  typeof error === 'object' && error && 'message' in error ? String(error.message) : 'Callable request failed.'

const toCallableError = (params: Omit<CallableError, 'code' | 'message'> & { error?: unknown; code?: FunctionsErrorCode; message?: string }): CallableError => ({
  code: params.code ?? errorCode(params.error),
  message: params.message ?? errorMessage(params.error),
  details: typeof params.error === 'object' && params.error && 'details' in params.error ? params.error.details : undefined,
  handler: params.handler,
  attempt: params.attempt,
  totalAttempts: params.totalAttempts,
  latencyMs: params.latencyMs,
  idempotencyKey: params.idempotencyKey,
  cause: params.error,
})

const wait = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal?.aborted) return reject(new DOMException('Callable request aborted.', 'AbortError'))
  const onAbort = () => {
    clearTimeout(timer)
    reject(new DOMException('Callable request aborted.', 'AbortError'))
  }
  const done = () => {
    signal?.removeEventListener('abort', onAbort)
    resolve()
  }
  const timer = setTimeout(done, ms)
  signal?.addEventListener('abort', onAbort, { once: true })
})

const raceWithTimeoutAndAbort = <T>(promise: Promise<T>, timeoutMs: number, signal: AbortSignal | undefined, timeoutError: () => CallableError, abortError: () => CallableError) =>
  new Promise<T>((resolve, reject) => {
    if (signal?.aborted) return reject(abortError())
    const timeout = setTimeout(() => finish(() => reject(timeoutError())), timeoutMs)
    const onAbort = () => finish(() => reject(abortError()))
    const finish = (callback: () => void) => {
      clearTimeout(timeout)
      signal?.removeEventListener('abort', onAbort)
      callback()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
    promise.then((value) => finish(() => resolve(value)), (error) => finish(() => reject(error)))
  })

const withMetadata = <TRequest>(payload: TRequest, metadata: Record<string, unknown>) =>
  (payload && typeof payload === 'object' && !Array.isArray(payload)
    ? { ...payload as object, __meta: metadata }
    : { value: payload, __meta: metadata }) as TRequest

export async function callable<TRequest, TResponse>(handlerName: string, payload: TRequest, options: CallableOptions = {}): Promise<TResponse> {
  const timeoutMs = options.timeoutMs ?? 30_000
  const retries = options.retries ?? 2
  const totalAttempts = retries + 1
  const retryOnCodes = options.retryOnCodes ?? DEFAULT_RETRY_CODES
  const idempotencyKey = options.idempotencyKey ?? createIdempotencyKey()

  const workspaceId = (payload && typeof payload === 'object' && 'workspaceId' in payload)
    ? (payload as { workspaceId?: unknown }).workspaceId
    : undefined

  if (typeof workspaceId === 'string' && isEduDemoWorkspace(workspaceId)) {
    const responder = getEduDemoResponder(handlerName)
    if (responder) {
      return (await responder(payload)) as TResponse
    }
    throw toCallableError({
      code: 'failed-precondition',
      message: 'PREVIEW_READ_ONLY',
      handler: handlerName,
      attempt: 1,
      totalAttempts: 1,
      latencyMs: 0,
      idempotencyKey,
    })
  }

  const debugSessionId = getActiveSessionId()
  const startedAt = Date.now()
  const requestPayload = withMetadata(payload, {
    idempotencyKey,
    clientTs: new Date().toISOString(),
    appVersion: import.meta.env.VITE_APP_VERSION ?? 'unknown',
    buildId: import.meta.env.VITE_BUILD_ID ?? 'unknown',
    ...(debugSessionId ? { debugSessionId } : {}),
  })
  const functions =
    options.functions ??
    (options.region
      ? (options.region !== REGION ? getFunctions(firebaseApp, options.region) : firebaseFunctions)
      : (OPS_CALLABLE_HANDLERS.has(handlerName) ? getCachedOpsCallableFunctions() : firebaseFunctions))

  let finalError: CallableError | undefined
  for (let index = 0; index < totalAttempts; index += 1) {
    const attempt = index + 1
    const latencyMs = () => Date.now() - startedAt
    const abort = () => toCallableError({ code: 'cancelled', message: 'Callable request aborted.', handler: handlerName, attempt, totalAttempts, latencyMs: latencyMs(), idempotencyKey })
    try {
      const response = await raceWithTimeoutAndAbort(
        httpsCallable<TRequest, TResponse>(functions, handlerName, { timeout: timeoutMs })(requestPayload),
        timeoutMs,
        options.signal,
        () => toCallableError({ code: 'deadline-exceeded', message: `Callable request timed out after ${timeoutMs}ms.`, handler: handlerName, attempt, totalAttempts, latencyMs: latencyMs(), idempotencyKey }),
        abort,
      )
      if (debugSessionId && !options.suppressLogging) telemetryLogger.debug('callable.success', { handler: handlerName, latencyMs: latencyMs(), attempt, debugSessionId })
      return response.data
    } catch (error) {
      finalError = (error as CallableError)?.handler === handlerName
        ? error as CallableError
        : toCallableError({ error, handler: handlerName, attempt, totalAttempts, latencyMs: latencyMs(), idempotencyKey })
      if (finalError.code === 'cancelled' || !retryOnCodes.includes(finalError.code) || attempt === totalAttempts) break
      const backoffMs = Math.min(2 ** index * 200, 4000) * (0.8 + Math.random() * 0.4)
      try {
        await wait(backoffMs, options.signal)
      } catch {
        finalError = abort()
        break
      }
    }
  }

  const failure = finalError ?? toCallableError({ code: 'internal', handler: handlerName, attempt: totalAttempts, totalAttempts, latencyMs: Date.now() - startedAt, idempotencyKey })
  if (!options.suppressLogging) telemetryLogger.error('callable.failure', { handler: handlerName, code: failure.code, latencyMs: failure.latencyMs, attempts: failure.attempt, idempotencyKey })
  throw failure
}
